import React, { Fragment } from 'react';
import { Lightbulb } from 'lucide-react';
import { cn } from '../../lib/utils';

const SKELETON_WIDTHS = ['w-[92%]', 'w-[78%]', 'w-[65%]'];

const BOLD_PATTERN = /(\*\*[^*]+\*\*)/g;

function renderInsightFinding(text) {
  if (!text || typeof text !== 'string') return null;

  return text.split(BOLD_PATTERN).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong
          key={index}
          className="font-semibold text-gray-900 dark:text-gray-100"
        >
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <Fragment key={index}>{part}</Fragment>;
  });
}

const InsightSkeleton = () => (
  <ul className="space-y-2.5 px-3 py-2.5" aria-hidden>
    {SKELETON_WIDTHS.map((widthClass) => (
      <li key={widthClass} className="flex gap-2">
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-gray-600" />
        <span
          className={cn(
            'h-3.5 animate-pulse rounded bg-gray-200 dark:bg-gray-700',
            widthClass
          )}
        />
      </li>
    ))}
  </ul>
);

const InsightBlock = ({ insight, loading = false }) => {
  const findings = Array.isArray(insight?.key_findings)
    ? insight.key_findings.filter(Boolean)
    : [];

  if (!loading && findings.length === 0) return null;

  return (
    <div
      className={cn(
        'mt-4 rounded-lg border border-gray-200 bg-gray-50/80',
        'dark:border-gray-700 dark:bg-gray-800/40'
      )}
      aria-busy={loading}
      aria-live="polite"
    >
      <div className="flex items-center gap-1.5 px-3 pt-2.5">
        <Lightbulb
          size={13}
          className={cn(
            'shrink-0 text-gray-500 dark:text-gray-400',
            loading && 'animate-pulse'
          )}
          aria-hidden
        />
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          Insights
        </span>
      </div>
      {loading ? (
        <InsightSkeleton />
      ) : (
        <ul className="space-y-1.5 px-3 pb-2.5 pt-1.5">
          {findings.map((finding, index) => (
            <li
              key={`${index}-${finding}`}
              className="flex gap-2 text-sm leading-relaxed text-gray-800 dark:text-gray-200"
            >
              <span
                className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500"
                aria-hidden
              />
              <span>{renderInsightFinding(finding)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default InsightBlock;
